import { CardBenefitsComponents as Components } from '../components'
import { View, Text } from 'react-native'
import { Button } from '@/src/common/components/Button'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { useLogError } from '@/src/common/hooks/useLogError'
import { GET_USER_INFO } from '@/src/common/gql/queries/user'
import { Check } from '@/src/assets/icons/Check'
import { useEffect, useRef } from 'react'
import { router } from 'expo-router'
import { triggerSegmentAction } from '@/src/services/segment'
import { useEvent } from 'tamagui'
import BottomSheet from '@gorhom/bottom-sheet'
import { CardBenefitsComponentsEnrollmentSuccessModal } from '../components/enrollment-success-modal'
import {
  BenefitModal,
  useIsBenefitScreenPresentedAsModal,
} from '@/src/experiences/card-benefits/components/BenefitModal'
import { vibrateSuccess } from '@/src/common/utils/vibrate'
import { mesaColorNames, fontFamilies } from '@/src/common/theme'
import { useBenefitsEnrollments } from '../hooks/useBenefitsEnrollments'
import { useBenefitEnroll } from '../hooks/useBenefitEnroll'
import { useMyLowesRewardsEnrollment } from '../hooks/useMyLowesRewardsEnrollment'
const selectedBenefit = {
  live: true,
  header: "MyLowe's Rewards Member Exclusive Benefit",
  preview_img: 'https://assets.mesamember.com/card_benefits/preview+Lowes.png',
  thumbnail_img: 'https://assets.mesamember.com/card_benefits/thumbnail+Lowes.png',
  title: "Join MyLowe's Rewards",
  summary:
    "This benefit is exclusive to MyLowe's Rewards members. It's quick and easy to join! Unlock All the Benefits of Your MyLowe's Rewards Membership \n\nWhat you get with MyLowe's Rewards:",
  how_to_redeem: [
    {
      text: "Earn Points Toward MyLowe's Money¹",
      subtext: '500 points = $5 reward',
    },
    {
      text: 'FREE Standard Shipping',
      subtext: 'Starting with Silver Key status. \nNot available in AK and HI.',
    },
    {
      text: 'FREE Member Gifts',
      subtext: "Get access to member-only gifts just for \nbeing a MyLowe's Rewards member.",
    },
    {
      text: 'Exclusive Offers',
      subtext: "We've created some great offers just for you.",
    },
    {
      text: 'Points Boosters',
      subtext: 'Earn points faster.',
    },
  ],
  terms: [
    "1 Points are awarded on eligible purchases for orders that have been settled and fulfilled. Exclusions, restrictions, and more terms apply. Details at [My Lowe's Rewards Terms](https://www.lowes.com/mylowes-rewards-terms). Subject to change.",
    '\n\n',
    "***MyLowe's Rewards Free Standard Shipping: Applies to eligible items in contiguous U.S. only. Threshold calculated after applicable discounts, before taxes, installation, additional delivery & extended protection fees, if any. Restrictions & more terms apply. See [here](https://www.lowes.com/l/help/free-shipping) for details.",
  ],
  theme: {
    badgeBackgroundColor: 'rgba(1, 51, 161, 0.8)',
    backgroundColor: '#0133a1',
    color: 'white',
  },
} as const

export const MyLowesRewards = () => {
  const benefitName = "Lowe's Home Improvement"
  const Alert = useAlert("My Lowe's Rewards")
  const enrollmentModalRef = useRef<BottomSheet>(null)

  const logError = useLogError()

  // Will remove once we send ids over
  const lowesBenefitIds = [
    'ccd9e3ed-bb9f-4877-bbf6-4762079eb1d9',
    'df1ea190-125d-4dbd-97a3-4797c7df5476',
  ]

  const { data } = useLoggedInQuery(GET_USER_INFO)
  const user = data?.getUserInfo

  const [enrollInBenefitMutation, enrollInBenefitResult] = useBenefitEnroll()
  const { data: benefitsEnrollmentsData } = useBenefitsEnrollments()
  const myLowesRewardsStatus = useMyLowesRewardsEnrollment({ email: user?.email ?? '' })

  const isUserEnrolledInMyLowesRewards = useEvent(async () => {
    try {
      if (myLowesRewardsStatus.data) {
        await enrollInBenefitMutation({ params: { benefitName } })

        void triggerSegmentAction({
          component: 'Button',
          action: 'Benefits Lowes Enrolls',
        })

        try {
          await vibrateSuccess()
        } finally {
          enrollmentModalRef.current?.snapToIndex(0)
        }
        return { isEnrolled: myLowesRewardsStatus.data }
      } else {
        return { isEnrolled: false }
      }
    } catch (error) {
      logError(error)
      return { error, isEnrolled: false }
    }
  })

  useEffect(() => {
    if (user?.email) {
      void isUserEnrolledInMyLowesRewards()
    }
  }, [user?.email, isUserEnrolledInMyLowesRewards])

  const handleAlreadyHaveMyLowesRewardsOnPress = async () => {
    if (user?.email) {
      const { isEnrolled, error } = await isUserEnrolledInMyLowesRewards()

      if (error) {
        void triggerSegmentAction({
          component: 'Button',
          action: 'Benefits Lowes Enroll_Failed_Error',
        })
      }
      if (!isEnrolled) {
        void triggerSegmentAction({
          component: 'Button',
          action: 'Benefits Lowes Not_Enrolled',
        })
        Alert.alert(
          'Error',
          `We couldn't find your MyLowe's Rewards account associated with ${user.email}, please try again after enrolling into MyLowe's Rewards`,
        )
      }
    }
  }

  const isUserEnrolled =
    benefitsEnrollmentsData?.filter((benefitEnrollment) =>
      lowesBenefitIds.includes(benefitEnrollment.benefitId),
    ).length === 1

  const isBenefitScreenPresentedAsModal = useIsBenefitScreenPresentedAsModal()

  const enrollmentButtonDisabled = isUserEnrolled || enrollInBenefitResult.isLoading

  return (
    <BenefitModal screenName={'my-lowes-rewards'}>
      <Components.Container>
        <Components.Header title={selectedBenefit.header} screenName={'my-lowes-rewards'} />
        <Components.Scroll>
          <Components.Hero
            source={{ uri: selectedBenefit.thumbnail_img }}
            backgroundColor={selectedBenefit.theme.backgroundColor}
          />
          <View
            style={[
              { padding: 16, backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'] },
              !isBenefitScreenPresentedAsModal && {
                borderBottomLeftRadius: 16,
                borderBottomRightRadius: 16,
              },
            ]}
          >
            <Components.Title title={selectedBenefit.title} />
            <Components.Summary summary={selectedBenefit.summary} />
            <Components.LowesHowToRedeem howToRedeem={selectedBenefit.how_to_redeem} />

            <Button
              testID="enroll-in-my-lowes-rewards"
              containerStyle={{ marginTop: 24, paddingHorizontal: 16 }}
              disabled={enrollmentButtonDisabled}
              disabledStyle={{ borderWidth: 1 }}
              style={
                enrollmentButtonDisabled && {
                  borderColor: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
                }
              }
              buttonStyle={{
                backgroundColor: isUserEnrolled
                  ? mesaColorNames['Mesa - Brand/White #FFFFFF']
                  : mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
              }}
              onPress={() =>
                router.push({ pathname: '/main/webview', params: { browse: 'my-lowes-rewards' } })
              }
              loading={enrollInBenefitResult.isLoading}
            >
              <>
                {isUserEnrolled && <Check height={20} width={20} />}
                <Text
                  style={[
                    {
                      fontFamily: fontFamilies['PlusJakartaSans-Bold'],
                      fontWeight: 700,
                      fontSize: 16,
                      marginLeft: 8,
                    },
                    isUserEnrolled && { color: mesaColorNames['Acre Turquoise/800 #086762'] },
                  ]}
                >
                  {isUserEnrolled ? `You're Enrolled` : `Join MyLowe's for Free`}
                </Text>
              </>
            </Button>

            {!isUserEnrolled && (
              <Button
                testID="already-have-my-lowes-rewards"
                containerStyle={{ marginTop: 8, paddingHorizontal: 16 }}
                buttonStyle={{ backgroundColor: mesaColorNames['Mesa - Brand/White #FFFFFF'] }}
                onPress={() => void handleAlreadyHaveMyLowesRewardsOnPress()}
              >
                <View
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <Text
                    style={{
                      fontFamily: fontFamilies['PlusJakartaSans-Bold'],
                      fontWeight: 700,
                      color: mesaColorNames['Mesa - Brand/Blackish #222222'],
                      fontSize: 16,
                      marginLeft: 8,
                    }}
                  >
                    I already have, I'd like to confirm
                  </Text>
                </View>
              </Button>
            )}
          </View>

          <Components.Terms terms={selectedBenefit.terms} />
        </Components.Scroll>

        {isUserEnrolled && (
          <CardBenefitsComponentsEnrollmentSuccessModal
            // @ts-expect-error on next release will delete UPDATED_BENEFITS_CMS
            selectedBenefit={selectedBenefit}
            bottomSheetRef={enrollmentModalRef}
          />
        )}
      </Components.Container>
    </BenefitModal>
  )
}
